import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Zap, Loader2, AlertTriangle, CheckCircle2, MapPin, Info } from 'lucide-react';
import AnomalyChart from './components/AnomalyChart';
import AnomalyCard from './components/AnomalyCard';

// ✅ Базовые имена (нижний регистр, без суффиксов с/м)
const PARAM_GROUPS = {
    temperatura: { name: 'Температура', unit: '°C', params: ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8', 'tx'] },
    davlenie: { name: 'Давление', unit: 'МПа', params: ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8', 'px'] },
    rasxod: { name: 'Расход', unit: 'м³', params: ['go1', 'go2', 'go3', 'go4', 'gox', 'g1', 'g2'] },
};

// ✅ Нормализация: "t1с" → "t1", "T1" → "t1", "Go1м" → "go1"
const normalizeParam = (name) => (name || '').replace(/[см]$/, '').toLowerCase();

const AnomalyDetectionTab = ({ selectedDevice }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [anomalies, setAnomalies] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [selectedParamGroup, setSelectedParamGroup] = useState('temperatura');
    const [threshold, setThreshold] = useState(30);
    const [appliedThreshold, setAppliedThreshold] = useState(null);
    const [error, setError] = useState(null);

    // ✅ Автозапуск только при смене устройства
    useEffect(() => {
        if (selectedDevice) {
            detectAnomalies();
        } else {
            setAnomalies([]);
            setChartData([]);
            setAppliedThreshold(null);
        }
    }, [selectedDevice]);

    const detectAnomalies = async () => {
        if (!selectedDevice) return;
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

            const response = await axios.get(`${API_URL}/api/analysis/anomalies`, {
                params: {
                    deviceName: selectedDevice.name,
                    deviceType: selectedDevice.deviceType,
                    days: 30,
                    threshold: threshold,
                },
                headers: { Authorization: `Bearer ${token}` },
            });

            setAnomalies(response.data.anomalies || []);
            setChartData(response.data.chartData || []);
            setAppliedThreshold(threshold);
        } catch (err) {
            console.error('Ошибка обнаружения аномалий:', err);
            setError(err.response?.data?.message || 'Не удалось выполнить анализ');
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Ключи графика берём ИЗ ФАКТИЧЕСКИХ данных и сопоставляем группе
    const chartDataKeys = useMemo(() => {
        if (!chartData.length) return [];
        const allKeys = new Set();
        chartData.forEach(row => Object.keys(row).forEach(k => k !== 'date' && allKeys.add(k)));
        return [...allKeys]
            .filter(k => PARAM_GROUPS[selectedParamGroup]?.params.includes(normalizeParam(k)))
            .sort()
            .map(k => ({ key: k, name: k }));
    }, [chartData, selectedParamGroup]);

    const filteredAnomalies = useMemo(() =>
        anomalies.filter(a =>
            PARAM_GROUPS[selectedParamGroup]?.params.includes(normalizeParam(a.paramName))
        ), [anomalies, selectedParamGroup]);

    if (!selectedDevice) {
        return (
            <div className="bg-slate-800/50 border border-slate-700 border-dashed rounded-xl p-12 text-center">
                <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">
                    Выберите устройство из списка выше для начала анализа
                </p>
            </div>
        );
    }

    const isThresholdDirty = appliedThreshold !== null && threshold !== appliedThreshold;

    return (
        <div className="space-y-4">
            {/* Панель параметров */}
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                        <label className="block text-slate-400 text-xs mb-1">
                            Порог изменения: <span className="text-white font-semibold">{threshold}%</span>
                            {appliedThreshold !== null && (
                                <span className="ml-2 text-slate-500">(применён: {appliedThreshold}%)</span>
                            )}
                        </label>
                        <input
                            type="range"
                            min="10"
                            max="100"
                            step="5"
                            value={threshold}
                            onChange={(e) => setThreshold(Number(e.target.value))}
                            className="w-full max-w-xs accent-red-500"
                        />
                    </div>
                    <button
                        onClick={detectAnomalies}
                        disabled={isLoading}
                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition disabled:opacity-50 flex items-center gap-2 ${isThresholdDirty
                                ? 'bg-red-500 text-white shadow-lg shadow-red-900/40'
                                : 'bg-red-600 hover:bg-red-500 text-white'
                            }`}
                    >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        Выполнить анализ
                    </button>
                </div>

                {isThresholdDirty && !isLoading && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                        <Info className="w-4 h-4 flex-shrink-0" />
                        Порог изменён ({appliedThreshold}% → {threshold}%).
                        Нажмите «Выполнить анализ», чтобы применить.
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {/* Переключатель групп параметров */}
            <div className="flex flex-wrap gap-2">
                {Object.entries(PARAM_GROUPS).map(([key, group]) => {
                    const count = filteredAnomalies.length;
                    return (
                        <button
                            key={key}
                            onClick={() => setSelectedParamGroup(key)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${selectedParamGroup === key
                                    ? 'bg-red-600 text-white'
                                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                }`}
                        >
                            {group.name} ({group.unit})
                            {selectedParamGroup === key && count > 0 && (
                                <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Сводка */}
            {!isLoading && (
                <div className="flex items-center gap-4 px-2">
                    {filteredAnomalies.length > 0 ? (
                        <div className="flex items-center gap-2 text-red-400 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            Обнаружено аномалий: <span className="font-bold">{filteredAnomalies.length}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-green-400 text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            Аномалий не обнаружено
                        </div>
                    )}
                </div>
            )}

            {/* График */}
            <AnomalyChart
                title={`Суточные показания — ${PARAM_GROUPS[selectedParamGroup].name}`}
                data={chartData}
                dataKeys={chartDataKeys}
                anomalies={filteredAnomalies}
                unit={PARAM_GROUPS[selectedParamGroup].unit}
            />

            {/* ✅ Карточки аномалий — с единицами измерения */}
            {filteredAnomalies.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredAnomalies.map((anomaly, idx) => (
                        <AnomalyCard
                            key={idx}
                            anomaly={anomaly}
                            unit={PARAM_GROUPS[selectedParamGroup].unit}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AnomalyDetectionTab;